"use client";

import { useState } from "react";
import { Wand2, Lock } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Floating Magic Editor launcher.
 * - md and up: round 56px button on the right edge, vertically centred (unchanged).
 * - mobile: small 40px button stacked above the chat-bot launcher, so it never
 *   sits on top of the SOAP note text.
 * Hidden on mobile while the product tour is running (see `.tour-hide-on-mobile`).
 */
export default function MagicEditorButton({
  onClick,
  disabled = false,
  lockMessage = "Please upgrade to use Magic Editor.",
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
  };

  const tooltipText = disabled ? lockMessage : "Magic Editor";

  return (
    <div className="tour-hide-on-mobile fixed right-6 bottom-[8.5rem] z-40 md:bottom-auto md:top-1/2 md:-translate-y-1/2">
      <Button
        onClick={handleClick}
        disabled={disabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          w-10 h-10 md:w-14 md:h-14 p-0 rounded-full shadow-lg transition-all duration-300
          ${
            disabled
              ? "bg-gray-300 cursor-not-allowed opacity-70 hover:bg-gray-300 scale-100"
              : `bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg ${
                  isHovered ? "md:scale-110 shadow-xl" : "scale-100"
                }`
          }
        `}
        title={tooltipText}
        aria-label={tooltipText}
      >
        {disabled ? (
          <Lock className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
        ) : (
          <Wand2 className="w-5 h-5 md:w-6 md:h-6 text-white" />
        )}
      </Button>

      {isHovered && (
        <div className="hidden md:block absolute right-16 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg max-w-[220px] whitespace-normal">
          {tooltipText}
          <div className="absolute right-0 top-1/2 transform translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
        </div>
      )}
    </div>
  );
}
